/**
 * Brand switcher + entry-brand attribution.
 *
 * Brand data is driven by the `brands` metaobject and injected by
 * `snippets/brand-switcher-assets.liquid` via a
 * <script type="application/json" id="brand-switcher-config"> block:
 *
 *   handle     — metaobject handle. Drives data-active-brand + theming, and is
 *                the collection-URL segment we match on (/collections/<handle>).
 *   shortName  — `brand_short_name`. Matched against product tags on a PDP.
 *   entryCode  — `brand_short_name | handleize`. The `entry_brand` cart value
 *                that Shopify Flow / ECS keys on.
 *   isDefault  — `is_default_brand`. The brand we fall back to visually.
 *
 * Loaded with `defer` and guarded so it is safe to execute more than once.
 */
(() => {
    const STORAGE_KEY = 'activeBrand';
    const ENTRY_BRAND_KEY = 'entryBrand';
    const ENTRY_CHECKED_KEY = 'entryBrandChecked';

    // Edmunds Roses runs on its own storefront, so it is pinned here rather than
    // read from the metaobject — its detection must not depend on that data.
    const EDMUNDS = {
        handle: 'edmunds-roses',
        shortName: 'Edmunds Roses',
        entryCode: 'edmunds-roses',
        isDefault: false
    };

    const configEl = document.getElementById('brand-switcher-config');
    const config = configEl ? JSON.parse(configEl.textContent) : {};
    const PAGE_TYPE = config.pageType || 'other';
    const PRODUCT_TAGS = config.productTags || [];

    // Metaobject brands first, then the pinned Edmunds entry. A metaobject brand
    // sharing the Edmunds handle is dropped so the pinned definition always wins.
    const METAOBJECT_BRANDS = (config.brands || []).filter((b) => b && b.handle && b.handle !== EDMUNDS.handle);
    const BRANDS = [...METAOBJECT_BRANDS, EDMUNDS];

    // A brand with no short name yet falls back to its handle so detection still
    // works, though the cart value will be the handle until the field is filled.
    const entryCodeOf = (brand) => brand.entryCode || brand.handle;

    // Product tags are matched case-insensitively against `brand_short_name`.
    const TAG_TO_BRAND = {};
    BRANDS.forEach((brand) => {
        if (brand.shortName) TAG_TO_BRAND[brand.shortName.trim().toLowerCase()] = entryCodeOf(brand);
    });

    // Both the brand handle and its entry code are valid collection-URL segments
    // (e.g. `roots` and `roots-and-rhizomes` both belong to Roots and Rhizomes).
    const COLLECTION_PREFIX_TO_BRAND = {};
    BRANDS.forEach((brand) => {
        [brand.handle, brand.entryCode].forEach((segment) => {
            if (segment) COLLECTION_PREFIX_TO_BRAND['/collections/' + segment] = entryCodeOf(brand);
        });
    });
    // Longest first, so a specific brand beats a shorter, more generic one.
    const COLLECTION_PREFIXES = Object.keys(COLLECTION_PREFIX_TO_BRAND).sort((a, b) => b.length - a.length);

    // Entry code -> metaobject handle, for driving the visual brand.
    const ENTRY_TO_HANDLE = {};
    BRANDS.forEach((brand) => {
        ENTRY_TO_HANDLE[entryCodeOf(brand)] = brand.handle;
    });

    // The visual fallback: the brand flagged `is_default_brand`, else the first
    // metaobject brand. Null only when the metaobject has no brands at all, in
    // which case the switcher isn't rendered either.
    const defaultBrand = METAOBJECT_BRANDS.find((b) => b.isDefault) || METAOBJECT_BRANDS[0];
    const DEFAULT_ACTIVE_BRAND = defaultBrand ? defaultBrand.handle : null;

    // True only when `prefix` is followed by a path boundary, so
    // `/collections/jung` matches `/collections/jung` and `/collections/jung-veg`
    // but NOT `/collections/jungle-mix`.
    const matchesPrefix = (path, prefix) => {
        if (path.indexOf(prefix) !== 0) return false;
        const next = path.charAt(prefix.length);
        return next === '' || next === '/' || next === '-';
    };

    // Reads a brand from a `/collections/<handle>/...` URL — used directly for a
    // PLP, and as the PDP fallback when the product is viewed inside a collection
    // (e.g. /collections/jung-seeds/products/...).
    const detectBrandFromPath = () => {
        const path = window.location.pathname.toLowerCase();
        const prefix = COLLECTION_PREFIXES.find((p) => matchesPrefix(path, p));
        return prefix ? COLLECTION_PREFIX_TO_BRAND[prefix] : null;
    };

    // Detection runs ONLY on PDP/PLP. Returns the matched brand entry code, or
    // null when the page carries no unambiguous brand signal.
    //  - PDP: exactly one brand tag wins; zero/multiple (multi-brand) falls back
    //    to the collection segment in the URL, then null.
    //  - PLP: the collection handle, then null.
    const detectMatchedBrand = () => {
        if (PAGE_TYPE === 'product') {
            const matches = [...new Set(
                PRODUCT_TAGS
                    .map((tag) => TAG_TO_BRAND[String(tag).trim().toLowerCase()])
                    .filter(Boolean)
            )];
            return matches.length === 1 ? matches[0] : detectBrandFromPath();
        }

        if (PAGE_TYPE === 'collection') {
            return detectBrandFromPath();
        }

        return null;
    };

    const applyBrand = (handle) => {
        document.documentElement.setAttribute('data-active-brand', handle);

        document.querySelectorAll('.brand-switcher [data-brand-handle]').forEach((el) => {
            const isActive = el.getAttribute('data-brand-handle') === handle;
            el.classList.toggle('active', isActive);
            el.classList.toggle('pointer-events-none', isActive);
            if (isActive) {
                el.setAttribute('aria-current', 'page');
                el.setAttribute('aria-disabled', 'true');
            } else {
                el.removeAttribute('aria-current');
                el.removeAttribute('aria-disabled');
            }
        });
    };

    const writeEntryBrandToCart = async (brand) => {
        try {
            await fetch('/cart/update.js', {
                method: 'POST',
                credentials: 'same-origin',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ attributes: { entry_brand: brand } })
            });
        } catch (error) {
            /* attribution is non-critical; never block the page */
        }
    };

    // entryBrand is sticky per session, and sessionStorage is the source of
    // truth for that stickiness. We only write when a brand is actually
    // detected; an undetected PDP/PLP leaves it unset so the next branded
    // PDP/PLP can still capture it. Because carts outlive sessions, the first
    // time a session fails to detect a brand we also clear any stale attribute a
    // previous session left behind, so Flow's downstream default applies cleanly.
    const captureEntryBrand = async (entryBrand) => {
        if (sessionStorage.getItem(ENTRY_BRAND_KEY)) return;

        if (entryBrand) {
            sessionStorage.setItem(ENTRY_BRAND_KEY, entryBrand);
            writeEntryBrandToCart(entryBrand);
            return;
        }

        // No brand detected yet this session: once, clear any stale attribute a
        // previous session may have left on the cart (empty value removes it).
        if (sessionStorage.getItem(ENTRY_CHECKED_KEY)) return;
        sessionStorage.setItem(ENTRY_CHECKED_KEY, '1');

        try {
            const response = await fetch('/cart.js', { credentials: 'same-origin' });
            if (!response.ok) return;

            const cart = await response.json();
            const staleBrand = cart && cart.attributes && cart.attributes.entry_brand;
            if (staleBrand) {
                writeEntryBrandToCart('');
            }
        } catch (error) {
            /* attribution is non-critical; never block the page */
        }
    };

    const isPdpOrPlp = PAGE_TYPE === 'product' || PAGE_TYPE === 'collection';
    const matchedBrand = detectMatchedBrand();

    // Entry brand: only when a brand is actually detected (PDP tag / PLP handle).
    // Unbranded or multi-brand pages stay null so a later branded page can set it.
    const entryBrand = matchedBrand;

    // Active (visual) brand resolution. Any PDP/PLP switches the visual brand to
    // match the page — branded pages use their brand, unbranded ones fall back to
    // the default. Off PDP/PLP it's the server-marked brand page (incl. the
    // homepage = default brand), then the last stored choice, then the default.
    const pageHandle = isPdpOrPlp
        ? (matchedBrand ? ENTRY_TO_HANDLE[matchedBrand] : DEFAULT_ACTIVE_BRAND)
        : null;
    const serverActive = document.querySelector('.brand-switcher [data-brand-handle].active');
    const activeBrand = pageHandle
        || (serverActive && serverActive.getAttribute('data-brand-handle'))
        || sessionStorage.getItem(STORAGE_KEY)
        || DEFAULT_ACTIVE_BRAND;

    if (activeBrand) {
        sessionStorage.setItem(STORAGE_KEY, activeBrand);
        applyBrand(activeBrand);
    }

    // Listeners and cart writes must only happen once per document, even though
    // this script may be included twice.
    if (window.__brandSwitcherInit) return;
    window.__brandSwitcherInit = true;

    document.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-brand-handle]');
        if (!trigger) return;

        const clicked = trigger.getAttribute('data-brand-handle');
        if (!clicked) return;

        sessionStorage.setItem(STORAGE_KEY, clicked);
        applyBrand(clicked);
    });

    captureEntryBrand(entryBrand);
})();
